import React, { useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import { isDateInSelectedPeriod } from '../../../placeholders/periodSummaryUtils';
import TreasuryTable3 from '../treasury/TreasuryTable3';
import { Pill, Stacked, downloadCsv, formatDate, money } from '../treasury/treasuryUi';
import StakeholderDetailModal from './StakeholderDetailModal';
import { ROLE_CONFIG, ROLE_LABELS } from './stakeholderLabels';

export { ROLE_CONFIG, ROLE_LABELS };

const ACCENT_NEGATIVE = '#ea580c';

const periodStats = (row, selectedPeriod) => {
  let documents = 0;
  let net = 0;
  let total = 0;
  (row.monthly || []).forEach((bucket) => {
    if (!isDateInSelectedPeriod(`${bucket.period}-01`, selectedPeriod)) return;
    documents += Number(bucket.documents || 0);
    net += Number(bucket.net || 0);
    total += Number(bucket.total || 0);
  });
  return { documents, net, total };
};

const uniqueSorted = (values) =>
  Array.from(new Set(values.map((value) => String(value || '').trim()).filter(Boolean))).sort((a, b) =>
    a.localeCompare(b, 'es')
  );

/**
 * Tabla de stakeholders (estilo Libro de ventas) con ficha emergente de la contraparte.
 */
const StakeholdersPanel = ({ ui, isDarkMode = false, role, rows = [], loading = false, error = '', selectedPeriod }) => {
  const config = ROLE_CONFIG[role];
  const [detail, setDetail] = useState(null);

  const periodRows = useMemo(
    () => rows.map((row) => ({ ...row, period: periodStats(row, selectedPeriod) })),
    [rows, selectedPeriod]
  );

  const periodTotal = useMemo(
    () => periodRows.reduce((sum, row) => sum + row.period.total, 0),
    [periodRows]
  );
  const activeCount = periodRows.filter((row) => row.period.documents > 0).length;

  const otherRoles = (row) => (row.roles || []).filter((item) => item !== role);

  const masterColumn = {
    clientes: {
      key: 'master',
      label: config.masterLabel,
      sortValue: (row) => String(row.master?.segment || row.master?.industry || '').toLowerCase(),
      render: (row) =>
        row.master ? (
          <Stacked
            top={[row.master.segment, row.master.industry].filter(Boolean).join(' · ') || row.master.name}
            bottom={[row.master.commune, row.master.region].filter(Boolean).join(', ')}
            topColor={ui.text}
            muted={ui.muted}
          />
        ) : (
          <span style={{ color: ui.muted, fontStyle: 'italic' }}>Sin ficha CRM</span>
        ),
    },
    proveedores: {
      key: 'master',
      label: config.masterLabel,
      sortValue: (row) => String(row.master?.supplier_type || '').toLowerCase(),
      render: (row) =>
        row.master ? (
          <Stacked
            top={row.master.supplier_type || row.master.name}
            bottom={[row.master.status, row.master.payment_terms?.[0]].filter(Boolean).join(' · ')}
            topColor={ui.text}
            muted={ui.muted}
          />
        ) : (
          <span style={{ color: ui.muted, fontStyle: 'italic' }}>Sin ficha de proveedor</span>
        ),
    },
    colaboradores: {
      key: 'master',
      label: config.masterLabel,
      sortValue: (row) => String(row.master?.department || '').toLowerCase(),
      render: (row) =>
        row.master ? (
          <Stacked
            top={row.master.job_title || row.master.name}
            bottom={[row.master.department, row.master.status].filter(Boolean).join(' · ')}
            topColor={ui.text}
            muted={ui.muted}
          />
        ) : (
          <span style={{ color: ui.muted, fontStyle: 'italic' }}>Sin ficha de colaborador</span>
        ),
    },
  }[role];

  const columns = useMemo(() => {
    const list = [
      {
        key: 'name',
        label: config.subject.charAt(0).toUpperCase() + config.subject.slice(1),
        minWidth: 220,
        sortValue: (row) => String(row.name || '').toLowerCase(),
        render: (row) => (
          <Stacked top={row.name || 'Sin nombre'} bottom={row.rut || 'Sin RUT'} topColor={ui.text} muted={ui.muted} />
        ),
      },
    ];
    if (masterColumn) list.push(masterColumn);
    if (role === 'proveedores') {
      list.push({
        key: 'bank',
        label: 'Datos bancarios',
        sortValue: (row) => String(row.master?.bank_accounts?.[0]?.bank_name || '').toLowerCase(),
        render: (row) => {
          const bank = row.master?.bank_accounts?.[0];
          return bank ? (
            <Stacked
              top={bank.bank_name}
              bottom={[bank.account_type, bank.account_number].filter(Boolean).join(' · ')}
              topColor={ui.text}
              muted={ui.muted}
            />
          ) : (
            <span style={{ color: ui.muted }}>—</span>
          );
        },
      });
    }
    list.push(
      {
        key: 'documents',
        label: 'Documentos',
        align: 'right',
        defaultDir: 'desc',
        sortValue: (row) => row.period.documents,
        render: (row) => (
          <Stacked
            top={String(row.period.documents)}
            bottom={row.documents ? `${row.documents} en total` : 'Sin documentos'}
            topColor={ui.text}
            muted={ui.muted}
          />
        ),
      },
      {
        key: 'net',
        label: 'Neto',
        align: 'right',
        defaultDir: 'desc',
        sortValue: (row) => row.period.net,
        render: (row) => (row.period.documents ? money(row.period.net) : <span style={{ color: ui.muted }}>—</span>),
      },
      {
        key: 'total',
        label: config.amountLabel,
        align: 'right',
        defaultDir: 'desc',
        sortValue: (row) => row.period.total,
        render: (row) =>
          row.period.documents ? (
            <Stacked
              top={money(row.period.total)}
              bottom={periodTotal ? `${((row.period.total / periodTotal) * 100).toFixed(1)}% del total` : ''}
              topColor={row.period.total < 0 ? ACCENT_NEGATIVE : ui.text}
              muted={ui.muted}
            />
          ) : (
            <span style={{ color: ui.muted }}>—</span>
          ),
      },
      {
        key: 'last',
        label: 'Último documento',
        defaultDir: 'desc',
        sortValue: (row) => String(row.last_date || ''),
        render: (row) =>
          row.last_date ? (
            <Stacked
              top={formatDate(row.last_date)}
              bottom={row.first_date ? `desde ${formatDate(row.first_date)}` : ''}
              topColor={ui.text}
              muted={ui.muted}
            />
          ) : (
            <span style={{ color: ui.muted }}>—</span>
          ),
      }
    );
    if (role === 'colaboradores') {
      list.push({
        key: 'expenses',
        label: 'Rendiciones',
        align: 'right',
        defaultDir: 'desc',
        sortValue: (row) => Number(row.expense_reports?.total || 0),
        render: (row) =>
          row.expense_reports ? (
            <Stacked
              top={money(row.expense_reports.total)}
              bottom={`${row.expense_reports.count} rendición(es) · ${row.expense_reports.pending} pendiente(s)`}
              topColor={ui.text}
              muted={ui.muted}
            />
          ) : (
            <span style={{ color: ui.muted }}>—</span>
          ),
      });
    }
    list.push({
      key: 'roles',
      label: 'Otros roles',
      sortValue: (row) => otherRoles(row).length,
      render: (row) => {
        const others = otherRoles(row);
        return others.length ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {others.map((item) => (
              <Pill key={item} label={ROLE_LABELS[item]} tone="info" isDarkMode={isDarkMode} />
            ))}
          </div>
        ) : (
          <span style={{ color: ui.muted }}>—</span>
        );
      },
    });
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, ui.text, ui.muted, isDarkMode, periodTotal]);

  const filters = useMemo(() => {
    const list = [
      {
        key: 'activity',
        label: 'Actividad',
        minWidth: 170,
        options: [
          { value: 'with', label: 'Con documentos en el período' },
          { value: 'without', label: 'Sin documentos en el período' },
        ],
        predicate: (row, value) => (value === 'with' ? row.period.documents > 0 : row.period.documents === 0),
      },
    ];
    if (config.masterLabel) {
      list.push({
        key: 'master',
        label: config.masterLabel,
        minWidth: 140,
        options: [
          { value: 'with', label: 'Con ficha' },
          { value: 'without', label: 'Sin ficha' },
        ],
        predicate: (row, value) => (value === 'with' ? Boolean(row.master) : !row.master),
      });
    }
    if (role === 'clientes') {
      list.push({
        key: 'segment',
        label: 'Segmento',
        minWidth: 130,
        options: uniqueSorted(rows.map((row) => row.master?.segment)).map((value) => ({ value, label: value })),
        predicate: (row, value) => row.master?.segment === value,
      });
    }
    if (role === 'proveedores') {
      list.push({
        key: 'type',
        label: 'Tipo',
        minWidth: 140,
        options: uniqueSorted(rows.map((row) => row.master?.supplier_type)).map((value) => ({ value, label: value })),
        predicate: (row, value) => row.master?.supplier_type === value,
      });
    }
    if (role === 'colaboradores') {
      list.push({
        key: 'department',
        label: 'Área',
        minWidth: 140,
        options: uniqueSorted(rows.map((row) => row.master?.department)).map((value) => ({ value, label: value })),
        predicate: (row, value) => row.master?.department === value,
      });
    }
    list.push({
      key: 'roles',
      label: 'Otros roles',
      minWidth: 130,
      options: [
        { value: 'multi', label: 'Con otros roles' },
        { value: 'single', label: 'Solo este rol' },
      ],
      predicate: (row, value) => (value === 'multi' ? otherRoles(row).length > 0 : otherRoles(row).length === 0),
    });
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, rows, config.masterLabel]);

  const searchText = (row) =>
    [
      row.name,
      row.rut,
      row.id,
      row.master?.name,
      row.master?.business_name,
      row.master?.code,
      row.master?.segment,
      row.master?.industry,
      row.master?.region,
      row.master?.commune,
      row.master?.supplier_type,
      row.master?.job_title,
      row.master?.department,
      row.master?.email,
    ].join(' ');

  const handleExport = (visibleRows) => {
    downloadCsv(
      `stakeholders_${role}_${new Date().toISOString().slice(0, 10)}.csv`,
      [
        'RUT',
        'Nombre',
        config.masterLabel || 'Ficha',
        'Documentos período',
        'Neto período',
        `${config.amountLabel} período`,
        'Documentos totales',
        'Facturas',
        'Notas de crédito',
        'Notas de débito',
        'Total histórico',
        'Primer documento',
        'Último documento',
        'Otros roles',
      ],
      visibleRows.map((row) => [
        row.rut,
        row.name,
        row.master ? row.master.code || row.master.name : '',
        row.period.documents,
        row.period.net,
        row.period.total,
        row.documents,
        row.invoices,
        row.credit_notes,
        row.debit_notes,
        row.total_amount,
        formatDate(row.first_date),
        formatDate(row.last_date),
        otherRoles(row)
          .map((item) => ROLE_LABELS[item])
          .join(', '),
      ])
    );
  };

  const subtitle = `${periodRows.length} ${config.title.toLowerCase()} · ${activeCount} con documentos en el período · ${
    config.amountLabel
  } ${money(periodTotal)}`;

  return (
    <>
      <TreasuryTable3
        rows={periodRows}
        loading={loading}
        error={error}
        isDarkMode={isDarkMode}
        title={config.title}
        subtitle={subtitle}
        columns={columns}
        defaultSort={{ key: 'total', dir: 'desc' }}
        searchPlaceholder="Nombre, RUT, código, segmento, región…"
        searchText={searchText}
        filters={filters}
        headerActions={[
          { key: 'export', label: 'Exportar', icon: Download, onClick: handleExport, disabled: !periodRows.length },
        ]}
        onRowClick={setDetail}
        emptyText={config.emptyText}
        minTableWidth={1080}
      />

      {detail ? (
        <StakeholderDetailModal
          ui={ui}
          isDarkMode={isDarkMode}
          role={role}
          stakeholder={detail}
          onClose={() => setDetail(null)}
        />
      ) : null}
    </>
  );
};

export default StakeholdersPanel;
